import { faCheck, faClose, faEdit, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { Button, Card, Col, Row, Stack } from "react-bootstrap";
import {
  deleteProgram,
  deleteProgramLevel,
  patchProgram,
  patchProgramLevel,
  postProgramLevel,
  reorderProgramLevels,
} from "../../../services/httpClient";
import Input from "../../ui/Input";
import { unsavedChanges } from "../../../App";
import IconEmbeddedButton from "../../ui/IconEmbeddedButton";
import ReorderEditDeleteCards from "../../ui/ReorderEditDeleteCards";

interface CardState {
  program: any;
  getItems: any;
  setOrgToSave: any;
  setErrMessage: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const ProgramCard = ({ program, getItems, setOrgToSave, setErrMessage, setShowToast, setToastText, setToastBody }: CardState) => {
  const [prog, setProg] = useState<any>(program);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [progName, setProgName] = useState<string>("");
  const [newLevel, setNewLevel] = useState<string>("");

  useEffect(() => {
    setProg(program);
    setProgName(program.name);
  }, [program]);

  const onClickEdit = () => {
    setIsAdding(false);
    setIsEditing(true);
  };

  const onClickEditFinal = () => {
    const updatedProg = {
      name: progName,
    };

    patchProgram(updatedProg, prog?.id)
      .then((res: any) => {
        unsavedChanges.value = false;
        setOrgToSave(false);
        setShowToast(true);
        setToastText("Program Updated");
        setToastBody(progName);
        setIsEditing(false);
        getItems(true);
      })
      .catch((err: any) => {
        setErrMessage(err?.response?.data?.msg);
        window.scrollTo({
          top: 0,
          behavior: "auto",
        });
      });
  };

  const onClickCancelEdit = () => {
    unsavedChanges.value = false;
    setOrgToSave(false);
    setIsEditing(false);
    setErrMessage(undefined);
  };

  const onUpdateName = (e: any) => {
    setProgName(e.target.value);
    unsavedChanges.value = true;
    setOrgToSave(true);
    setErrMessage(undefined);
  };

  const onClickDelete = () => {
    setIsDeleting(true);
  };

  const onClickDeleteFinal = () => {
    deleteProgram(prog?.id)
      .then((res: any) => {
        setShowToast(true);
        setToastText("Program Deleted");
        setToastBody(progName);
        setIsDeleting(false);
        getItems(true);
      })
      .catch((err: any) => {
        setIsDeleting(false);
        setErrMessage(err?.response?.data?.msg);
        window.scrollTo({
          top: 0,
          behavior: "auto",
        });
      });
  };

  const onClickCancelDelete = () => {
    setIsDeleting(false);
    setErrMessage(undefined);
  };

  // Level adding
  const onClickAdd = () => {
    setIsAdding(true);
    unsavedChanges.value = true;
    setOrgToSave(true);
    setErrMessage(undefined);
  };

  const onUpdateLevel = (e: any) => {
    setNewLevel(e.target.value);
    unsavedChanges.value = true;
    setOrgToSave(true);
    setErrMessage(undefined);
  };

  const onClickSaveLevel = (isDone: boolean = false) => {
    setErrMessage("");
    if (!newLevel || newLevel.trim() === "") {
      setErrMessage("Please enter a name for the program level.");
      return;
    }

    postProgramLevel(prog?.id, newLevel)
      .then((res: any) => {
        unsavedChanges.value = false;
        setOrgToSave(false);
        setShowToast(true);
        setToastText("Program Level Created");
        setToastBody(newLevel);
        if (isDone) {
          setIsAdding(false);
        }
        setNewLevel("");
        getItems(true);
      })
      .catch((err: any) => {
        setErrMessage(err?.response?.data?.msg);
        window.scrollTo({
          top: 0,
          behavior: "auto",
        });
      });
  };

  const onClickCancelAdd = () => {
    unsavedChanges.value = false;
    setOrgToSave(false);
    setIsAdding(false);
    setErrMessage("");
    setNewLevel("");
  };

  // Level editing
  const onClickEditLevelFinal = (levelId: any, levelName: string) => {
    setErrMessage("");

    patchProgramLevel(levelId, levelName)
      .then((res: any) => {
        setShowToast(true);
        setToastText("Program Level Updated");
        setToastBody(levelName);
        unsavedChanges.value = false;
        setOrgToSave(false);
        getItems(true);
      })
      .catch((err: any) => {
        setErrMessage(err?.response?.data?.msg);
      });
  };

  // Level deletion
  const onClickDeleteLevelFinal = (levelId: any, name: string) => {
    deleteProgramLevel(levelId)
      .then((res: any) => {
        setShowToast(true);
        setToastText("Program Level Deleted");
        setToastBody(name);
        getItems(true);
      })
      .catch((err: any) => {
        setErrMessage(err?.response?.data?.msg);
      });
  };

  // Renders
  const renderAdd = () => {
    return (
      <Stack gap={3}>
        <Row>
          <Col xs={6}>
            <Input label="Level" type="text" value={newLevel} hasFocus={true} onUpdate={(e: any) => onUpdateLevel(e)} required={true} />
          </Col>
        </Row>
        <Row>
          <Col xs={12} lg={8}>
            <Stack direction="horizontal" gap={3}>
              <Button className="app-secondary-color button" disabled={!newLevel} onClick={() => onClickSaveLevel(false)}>
                Save and Add Another
              </Button>
              <div className="vr" />
              <Button className="app-secondary-color button" disabled={!newLevel} onClick={() => onClickSaveLevel(true)}>
                Save and Done
              </Button>
              <div className="vr" />
              <Button className="app-primary-color button" onClick={() => onClickCancelAdd()}>
                Done
              </Button>
            </Stack>
          </Col>
        </Row>
      </Stack>
    );
  };

  const renderEdit = () => {
    return (
      <Card key={prog.name}>
        <Card.Body>
          <Stack direction="horizontal" gap={3}>
            <Col>
              <Input label="Program" type="text" value={progName} hasFocus={true} onUpdate={(e: any) => onUpdateName(e)} />
            </Col>
            <FontAwesomeIcon className="text-muted me-1 cursor-pointer" icon={faCheck} onClick={() => onClickEditFinal()} />
            <div className="vr" />
            <FontAwesomeIcon className="text-muted me-1 cursor-pointer" icon={faClose} onClick={() => onClickCancelEdit()} />
          </Stack>
        </Card.Body>
      </Card>
    );
  };

  const renderDelete = () => {
    return (
      <Card key={prog.name}>
        <Card.Body>
          <Stack direction="horizontal" gap={3}>
            <Stack style={{ justifyContent: "center" }} gap={2}>
              <Card.Subtitle>Delete {prog?.name}?</Card.Subtitle>
            </Stack>
            <Button className="app-danger-color button" onClick={() => onClickDeleteFinal()}>
              Confirm Delete
            </Button>
            <div className="vr" />
            <Button className="app-primary-color button" onClick={() => onClickCancelDelete()}>
              Cancel
            </Button>
          </Stack>
        </Card.Body>
      </Card>
    );
  };

  const updateLevelOrder = (newOrder: any) => {
    reorderProgramLevels(newOrder)
      .then(() => {
        setShowToast(true);
        setToastText("Program Level Order");
        setToastBody("The level order of " + prog?.name + " has been updated.");
        setProg({ ...prog, programLevels: newOrder });
        // setDraggedIndex(null);
      })
      .catch((err) => {
        setErrMessage(err?.response?.data?.msg);
      });
  };

  return (
    <div>
      {isDeleting ? (
        <>{renderDelete()}</>
      ) : (
        <>
          {isEditing ? (
            <>{renderEdit()}</>
          ) : (
            <Row>
              <Col xs={9}>
                <h6 style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{prog.name}</h6>
              </Col>
              <Col xs={3} className="d-flex justify-content-end">
                <Stack direction="horizontal" gap={3}>
                  <IconEmbeddedButton onClickButton={onClickAdd} tooltipText={"Add Level"} icon={faPlus} />
                  <div className="vr" />
                  <IconEmbeddedButton onClickButton={onClickEdit} tooltipText={"Edit Program"} icon={faEdit} />
                  <div className="vr" />
                  <IconEmbeddedButton onClickButton={onClickDelete} tooltipText={"Delete Program"} icon={faTrash} />
                </Stack>
              </Col>
            </Row>
          )}
          {isAdding && <>{renderAdd()}</>}
          {prog?.programLevels?.length > 0 && (
            <Stack gap={2} className="mt-3">
              <ReorderEditDeleteCards
                items={prog?.programLevels}
                editInputLabel="Level"
                onEditFinal={onClickEditLevelFinal}
                onDeleteFinal={onClickDeleteLevelFinal}
                updateOrder={updateLevelOrder}
                setErrMessage={setErrMessage}
              />
            </Stack>
          )}
        </>
      )}
    </div>
  );
};

export default ProgramCard;
